import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { AmountInput } from '../components/ui/amount-input';
import { RefreshCw, DollarSign, Lock, Eye, EyeOff, Wifi, TrendingUp, TrendingDown, Clock, Building2, Camera, Save, Trash2, Phone, Send, Linkedin, Cake, UserIcon, ExternalLink, Unlink, CheckCircle2, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Trans, useTranslation } from 'react-i18next';
import PaymentCardsCard from '../components/PaymentCardsCard';
import BillingProfilesCard from '../components/BillingProfilesCard';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { copyText as copyToClipboard } from '../lib/clipboard';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function Settings() {
  const { user, refreshUser } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useTranslation('settings');

  // Exchange rate state
  const [liveRate, setLiveRate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [converter, setConverter] = useState({ amount: '', from: 'USD', result: '' });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Profile edit state
  const [editName, setEditName] = useState('');
  const [profileData, setProfileData] = useState({
    first_name: '', last_name: '', phone: '', telegram: '', linkedin: '', birthday: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  // Telegram Bot link state
  const [telegramStatus, setTelegramStatus] = useState({ linked: false, has_employee: false, loading: true });
  const [telegramLinking, setTelegramLinking] = useState(false);
  // One-time deep link shown on screen ({ link, command, expiresAt }). Opening it
  // automatically after the request is blocked as a popup by Safari / mobile browsers.
  const [telegramLink, setTelegramLink] = useState(null);
  const [linkSecondsLeft, setLinkSecondsLeft] = useState(0);
  const linkPollRef = useRef(null);
  const [telegramUnlinking, setTelegramUnlinking] = useState(false);

  // Initialize profile from API
  useEffect(() => {
    if (user?.name) setEditName(user.name);
    fetchProfile();
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/profile`, { withCredentials: true });
      const d = res.data;
      setProfileData({
        first_name: d.first_name || '',
        last_name: d.last_name || '',
        phone: d.phone || '',
        telegram: d.telegram || '',
        linkedin: d.linkedin || '',
        birthday: d.birthday || '',
      });
      if (d.name) setEditName(d.name);
    } catch (err) {
      // silent — use defaults
    }
  };

  // Telegram link status
  const fetchTelegramStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/telegram/link-status`, { withCredentials: true });
      setTelegramStatus({ ...res.data, loading: false });
    } catch {
      setTelegramStatus(s => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchTelegramStatus();
  }, [fetchTelegramStatus]);

  const stopLinkPolling = () => {
    if (linkPollRef.current) clearInterval(linkPollRef.current);
    linkPollRef.current = null;
  };

  useEffect(() => stopLinkPolling, []);

  // Countdown for the on-screen link; hide it when it expires
  useEffect(() => {
    if (!telegramLink) return undefined;
    const tick = () => {
      const left = Math.max(0, Math.round((telegramLink.expiresAt - Date.now()) / 1000));
      setLinkSecondsLeft(left);
      if (left === 0) { setTelegramLink(null); stopLinkPolling(); }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [telegramLink]);

  const copyText = async (value, label) => {
    if (await copyToClipboard(value)) toast.success(t('copy.copied', { label }));
    else toast.error(t('copy.failed'));
  };

  const handleTelegramLink = async () => {
    setTelegramLinking(true);
    try {
      const res = await axios.post(`${API_URL}/api/telegram/link-token`, {}, { withCredentials: true });
      const link = res.data.link;
      const token = (link.split('start=')[1] || '').trim();
      setTelegramLink({ link, command: `/start ${token}`, expiresAt: Date.now() + res.data.expires_in * 1000 });
      toast.success(t('telegram.linkReady'));
      // Poll until the bot confirms the link
      stopLinkPolling();
      linkPollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API_URL}/api/telegram/link-status`, { withCredentials: true });
          if (s.data.linked) {
            stopLinkPolling();
            setTelegramLink(null);
            setTelegramStatus({ ...s.data, loading: false });
            toast.success(t('telegram.linkedToast'));
          }
        } catch { /* ignore */ }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik yuz berdi');
    } finally {
      setTelegramLinking(false);
    }
  };

  const handleTelegramUnlink = async () => {
    setTelegramUnlinking(true);
    try {
      await axios.post(`${API_URL}/api/telegram/unlink`, {}, { withCredentials: true });
      setTelegramStatus(s => ({ ...s, linked: false }));
      toast.success(t('telegram.unlinkedToast'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik yuz berdi');
    } finally {
      setTelegramUnlinking(false);
    }
  };

  const fetchLiveRate = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/exchange-rates/live`, { withCredentials: true });
      setLiveRate(response.data);
    } catch (error) {
      console.error('Error fetching live rate:', error);
      // Fallback: try the old endpoint
      try {
        const fallback = await axios.get(`${API_URL}/api/exchange-rates`, { withCredentials: true });
        if (fallback.data?.length > 0) {
          const usdToUzs = fallback.data.find(r => r.from_currency === 'USD' && r.to_currency === 'UZS');
          if (usdToUzs) {
            setLiveRate({ rate: usdToUzs.rate, source: usdToUzs.source || 'cbu', updated_at: usdToUzs.updated_at });
          }
        }
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    }
  }, []);

  useEffect(() => {
    fetchLiveRate();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchLiveRate, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchLiveRate]);

  const handleRefreshFromCBU = async () => {
    try {
      setRefreshing(true);
      const response = await axios.post(`${API_URL}/api/exchange-rates/refresh`, {}, { withCredentials: true });
      toast.success(t('rate.refreshed', { rate: Number(response.data.rate).toLocaleString() }));
      await fetchLiveRate();
    } catch (error) {
      console.error('Error refreshing from CBU:', error);
      const msg = error.response?.data?.detail || t('rate.refreshFailed');
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  const handleConvert = () => {
    const amount = parseFloat(converter.amount);
    if (isNaN(amount)) {
      setConverter({ ...converter, result: '' });
      return;
    }
    const rate = liveRate?.rate || 12500;
    if (converter.from === 'USD') {
      const result = amount * rate;
      setConverter({ ...converter, result: `${result.toLocaleString()} UZS` });
    } else {
      const result = amount / rate;
      setConverter({ ...converter, result: `$${result.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` });
    }
  };

  const toggleConverterCurrency = () => {
    setConverter({
      ...converter,
      from: converter.from === 'USD' ? 'UZS' : 'USD',
      result: ''
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('password.mismatch'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error(t('password.tooShort'));
      return;
    }

    try {
      setChangingPassword(true);
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, { withCredentials: true });

      toast.success(t('password.changed'));
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.detail || t('password.changeFailed');
      toast.error(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveProfile = async () => {
    const trimmed = editName.trim();
    if (trimmed.length < 2) {
      toast.error(t('profile.nameTooShort'));
      return;
    }
    try {
      setSavingProfile(true);
      await axios.put(`${API_URL}/api/auth/profile`, {
        name: trimmed,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        phone: profileData.phone,
        telegram: profileData.telegram,
        linkedin: profileData.linkedin,
        birthday: profileData.birthday,
      }, { withCredentials: true });
      toast.success(t('profile.saved'));
      await refreshUser();
    } catch (error) {
      const msg = error.response?.data?.detail || t('profile.saveFailed');
      toast.error(msg);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error(t('avatar.tooLarge'));
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingAvatar(true);
      await axios.post(`${API_URL}/api/auth/profile/avatar`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('avatar.updated'));
      await refreshUser();
    } catch (error) {
      const msg = error.response?.data?.detail || t('avatar.uploadFailed');
      toast.error(msg);
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      setUploadingAvatar(true);
      await axios.delete(`${API_URL}/api/auth/profile/avatar`, { withCredentials: true });
      toast.success(t('avatar.deleted'));
      await refreshUser();
    } catch (error) {
      const msg = error.response?.data?.detail || t('avatar.deleteFailed');
      toast.error(msg);
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Format time ago
  const formatTimeAgo = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHrs / 24);

    if (diffMin < 1) return t('timeAgo.justNow');
    if (diffMin < 60) return t('timeAgo.minutes', { count: diffMin });
    if (diffHrs < 24) return t('timeAgo.hours', { count: diffHrs });
    return t('timeAgo.days', { count: diffDays });
  };

  const isOAuthUser = !user?.username && user?.provider === 'google';

  const diff = liveRate?.diff ? parseFloat(liveRate.diff) : null;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>{t('header.title')}</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>{t('header.subtitle')}</p>
      </div>

      {/* Live Exchange Rate Card */}
      <Card className="border-emerald-200 dark:border-emerald-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-emerald-600" />
              {t('rate.title')}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-emerald-600 border-emerald-300 dark:border-emerald-700 flex items-center gap-1">
                <Wifi className="h-3 w-3" />
                {t('rate.badge')}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Live Rate Display */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl p-4 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  {t('rate.cbuRate')}
                </p>
                <div className="flex flex-wrap items-baseline gap-x-3 mt-2">
                  <span className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">
                    1 USD = {liveRate?.rate ? Number(liveRate.rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '...'}
                  </span>
                  <span className="text-lg text-slate-500 dark:text-slate-400">UZS</span>
                </div>
                {diff !== null && (
                  <div className={`flex items-center gap-1 mt-2 text-sm ${diff >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {diff >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                    <span>{t('rate.change', { diff: `${diff >= 0 ? '+' : ''}${diff}` })}</span>
                  </div>
                )}
                {liveRate?.cbu_date && (
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                    {t('rate.cbuDate', { date: liveRate.cbu_date })}
                  </p>
                )}
              </div>
              <div className="sm:text-right">
                {liveRate?.updated_at && (
                  <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatTimeAgo(liveRate.updated_at)}
                  </p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={handleRefreshFromCBU}
                  disabled={refreshing}
                  data-testid="refresh-cbu-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? t('rate.refreshing') : t('rate.refresh')}
                </Button>
              </div>
            </div>
          </div>

          <Separator />

          {/* Quick Converter */}
          <div className="space-y-4">
            <Label>{t('converter.label')}</Label>
            <div className="flex flex-wrap items-center gap-3">
              <AmountInput
                value={converter.amount}
                onChange={(val) => setConverter({ ...converter, amount: val, result: '' })}
                placeholder={t('converter.placeholder', { currency: converter.from })}
                className="max-w-[180px]"
                data-testid="converter-amount-input"
              />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {converter.from}
              </span>
              <Button variant="outline" size="sm" onClick={toggleConverterCurrency} data-testid="swap-currency-btn">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button onClick={handleConvert} data-testid="convert-btn">
                {t('converter.convert')}
              </Button>
              {converter.result && (
                <div className="px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <span className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">
                    = {converter.result}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Change Section */}
      {!isOAuthUser && (
        <Card className="border-amber-200 dark:border-amber-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-600" />
              {t('password.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="current-password">{t('password.current')}</Label>
                <div className="relative mt-2">
                  <Input
                    id="current-password"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    placeholder={t('password.currentPlaceholder')}
                    required
                    data-testid="current-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="new-password">{t('password.new')}</Label>
                <div className="relative mt-2">
                  <Input
                    id="new-password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder={t('password.newPlaceholder')}
                    required
                    data-testid="new-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm-password">{t('password.confirm')}</Label>
                <div className="relative mt-2">
                  <Input
                    id="confirm-password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder={t('password.confirmPlaceholder')}
                    required
                    data-testid="confirm-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <Button type="submit" disabled={changingPassword} data-testid="change-password-btn">
                <Lock className="h-4 w-4 mr-2" />
                {changingPassword ? t('password.changing') : t('password.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Profile Information - Editable */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-slate-600" />
            {t('profile.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar section */}
          <div className="flex items-center gap-6">
            <div className="relative group">
              <img
                src={user?.picture ? (user.picture.startsWith('/uploads') ? `${API_URL}${user.picture}` : user.picture) : 'https://via.placeholder.com/80?text=👤'}
                alt={user?.name}
                className="h-20 w-20 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700"
              />
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                aria-label={t('profile.changePhoto')}
                className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <Camera className="h-5 w-5 text-white" />
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </div>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => avatarInputRef.current?.click()} disabled={uploadingAvatar}>
                  <Camera className="h-3.5 w-3.5 mr-1.5" />
                  {uploadingAvatar ? t('profile.uploading') : t('profile.upload')}
                </Button>
                {user?.picture && (
                  <Button variant="outline" size="sm" onClick={handleDeleteAvatar} disabled={uploadingAvatar} className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    {t('common:actions.delete')}
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-500">{t('profile.formats')}</p>
            </div>
          </div>

          <Separator />

          {/* Name edit */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="profile-first-name">{t('profile.firstName')}</Label>
                <div className="relative mt-1.5">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="profile-first-name"
                    value={profileData.first_name}
                    onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                    placeholder={t('profile.firstNamePlaceholder')}
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="profile-last-name">{t('profile.lastName')}</Label>
                <div className="relative mt-1.5">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="profile-last-name"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                    placeholder={t('profile.lastNamePlaceholder')}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="profile-name">{t('profile.displayName')}</Label>
              <Input
                id="profile-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t('profile.displayNamePlaceholder')}
                className="mt-1.5 max-w-sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="profile-phone">{t('profile.phone')}</Label>
                <div className="relative mt-1.5">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="profile-phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="+998 90 123 45 67"
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="profile-telegram">{t('profile.telegram')}</Label>
                <div className="relative mt-1.5">
                  <Send className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="profile-telegram"
                    value={profileData.telegram}
                    onChange={(e) => setProfileData({ ...profileData, telegram: e.target.value })}
                    placeholder="@username"
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="profile-linkedin">{t('profile.linkedin')}</Label>
                <div className="relative mt-1.5">
                  <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="profile-linkedin"
                    value={profileData.linkedin}
                    onChange={(e) => setProfileData({ ...profileData, linkedin: e.target.value })}
                    placeholder="linkedin.com/in/username"
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="profile-birthday">{t('profile.birthday')}</Label>
                <div className="relative mt-1.5">
                  <Cake className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="profile-birthday"
                    type="date"
                    value={profileData.birthday}
                    onChange={(e) => setProfileData({ ...profileData, birthday: e.target.value })}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-slate-500">{t('common:fields.email')}</Label>
              <p className="text-sm text-slate-900 dark:text-slate-100 mt-1">{user?.email}</p>
            </div>
            <div>
              <Label className="text-slate-500">{t('profile.role')}</Label>
              <p className="text-sm text-slate-900 dark:text-slate-100 mt-1 capitalize">{user?.role ? t(`common:roles.${user.role}`, { defaultValue: user.role.replace('_', ' ') }) : ''}</p>
            </div>
            <div className="pt-2">
              <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full sm:w-auto">
                <Save className="h-4 w-4 mr-1.5" />
                {savingProfile ? t('common:status.saving') : t('profile.save')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards debtors repay to (shown in the Telegram bot) */}
      <PaymentCardsCard />

      {/* Issuer requisites for client invoices (finance staff) */}
      {user && !['employee', 'pending'].includes(user.role) && <BillingProfilesCard user={user} />}

      {/* Telegram Bot */}
      {(telegramStatus.has_employee || telegramStatus.loading) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              {t('telegram.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {telegramStatus.loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> {t('telegram.checking')}
              </div>
            ) : telegramStatus.linked ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">{t('telegram.linked')}</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('telegram.linkedHint')}
                </p>
                <Button variant="outline" size="sm" onClick={handleTelegramUnlink} disabled={telegramUnlinking}>
                  {telegramUnlinking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Unlink className="h-4 w-4 mr-1.5" />}
                  {t('telegram.unlink')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('telegram.intro')}
                </p>
                {!telegramLink ? (
                  <Button onClick={handleTelegramLink} disabled={telegramLinking}>
                    {telegramLinking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1.5" />}
                    {t('telegram.connect')}
                  </Button>
                ) : (
                  <div className="p-3 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50/60 dark:bg-sky-900/10 space-y-3" data-testid="telegram-link-panel">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('telegram.linkReadyTitle')}</span>
                      <span className="text-xs text-slate-500 tabular-nums">
                        {Math.floor(linkSecondsLeft / 60)}:{String(linkSecondsLeft % 60).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild>
                        <a href={telegramLink.link} target="_blank" rel="noopener noreferrer" data-testid="telegram-link-open">
                          <Send className="h-4 w-4 mr-1.5" /> {t('telegram.open')}
                        </a>
                      </Button>
                      <Button variant="outline" onClick={() => copyText(telegramLink.link, t('telegram.linkLabel'))}>
                        <Copy className="h-4 w-4 mr-1.5" /> {t('telegram.copyLink')}
                      </Button>
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                      <p><Trans t={t} i18nKey="telegram.instructions" components={{ b: <b /> }} /></p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 min-w-0 truncate px-2 py-1 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                          {telegramLink.command}
                        </code>
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => copyText(telegramLink.command, t('telegram.commandLabel'))} title={t('telegram.copy')}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <button type="button" className="text-xs text-slate-500 hover:underline" onClick={handleTelegramLink} disabled={telegramLinking}>
                      {t('telegram.newLink')}
                    </button>
                  </div>
                )}
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  {t('telegram.securityNote')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle>{t('appearance.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="dark-mode">{t('appearance.darkMode')}</Label>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('appearance.darkModeHint')}</p>
            </div>
            <Switch id="dark-mode" checked={isDark} onCheckedChange={toggleTheme} data-testid="dark-mode-toggle" />
          </div>
        </CardContent>
      </Card>

      {/* Language */}
      <Card data-testid="language-settings-card">
        <CardHeader>
          <CardTitle>{t('language.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>{t('language.interface')}</Label>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('language.description')}</p>
            </div>
            <LanguageSwitcher className="border border-slate-200 dark:border-slate-700" />
          </div>
        </CardContent>
      </Card>

      {/* System Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('system.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('system.version')}</p>
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">{process.env.REACT_APP_VERSION || 'dev'}</p>
          </div>
          <Separator />
          <div>
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('system.userId')}</p>
            <p className="text-sm text-slate-900 dark:text-slate-100 font-mono">{user?.user_id}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Settings;
